const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

export const safeApiText = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const text = value.trim();
  if (
    !text ||
    /\[object Object\]/i.test(text) ||
    /<\/?[a-z][\s\S]*>/i.test(text) ||
    /(?:^|\n)\s*(?:PHP\s+)?(?:warning|notice|deprecated|parse error|recoverable fatal error|fatal error|strict standards?)\s*:/i.test(
      text
    ) ||
    /(?:^|\n)\s*(?:stack trace:|call stack:|#\d+\s|thrown in\s)/i.test(text)
  ) {
    return "";
  }
  return text;
};

const parsePayload = (value) => {
  if (typeof value !== "string") {
    return asObject(value);
  }

  const text = value.trim();
  if (!text) {
    return {};
  }

  try {
    return asObject(JSON.parse(text));
  } catch (_) {
    // PHP can prepend diagnostics to an otherwise valid JSON response.
    // Recover only a complete JSON suffix and never expose the diagnostic.
    for (let index = 0; index < text.length; index += 1) {
      if (text[index] !== "{" && text[index] !== "[") {
        continue;
      }
      try {
        const recovered = JSON.parse(text.slice(index).trim());
        const payload = asObject(recovered);
        if (Object.keys(payload).length) {
          return payload;
        }
      } catch (ignore) {
        // Keep scanning for the actual response envelope.
      }
    }

    const message = safeApiText(text);
    return message ? { message } : {};
  }
};

export const getApiPayload = (source) => {
  if (
    source?.response &&
    Object.prototype.hasOwnProperty.call(source.response, "data")
  ) {
    return parsePayload(source.response.data);
  }

  if (
    source &&
    typeof source === "object" &&
    Object.prototype.hasOwnProperty.call(source, "data") &&
    (source.config ||
      source.headers ||
      typeof source.status === "number" ||
      ![
        "msg",
        "error",
        "core_error",
        "message",
        "message_key",
        "validate",
        "alert",
      ].some((key) => Object.prototype.hasOwnProperty.call(source, key)))
  ) {
    return parsePayload(source.data);
  }

  return parsePayload(source);
};

const translatedText = (key, translate, aliases) => {
  const safeKey = safeApiText(key);
  if (!safeKey || typeof translate !== "function") {
    return "";
  }

  const customAliases = aliases?.[safeKey];
  const candidates = [
    ...(Array.isArray(customAliases)
      ? customAliases
      : customAliases
      ? [customAliases]
      : []),
    safeKey,
    `tr_message.${safeKey}`,
    `tr_validate.${safeKey}`,
    `tr_status.${safeKey}`,
  ];

  for (const candidate of candidates) {
    try {
      const translated = safeApiText(
        translate(candidate, { defaultValue: "" })
      );
      if (translated && translated !== candidate) {
        return translated;
      }
    } catch (_) {
      // Translation lookup is optional; continue to the safe fallback.
    }
  }
  return "";
};

export const normalizeApiError = (source, options = {}) => {
  const payload = getApiPayload(source);
  const coreError = asObject(payload.core_error);
  const standardError = asObject(payload.error);
  const providerStatus = asObject(payload.ResultStatus);
  const nestedProviderStatus = asObject(
    asObject(payload.response).ResultStatus
  );
  const detail = Object.keys(coreError).length ? coreError : standardError;
  const context = asObject(detail.context);
  const translate = options.translate;
  const aliases = options.aliases || {};
  const msg = safeApiText(payload.msg);
  if (["PROVIDER_CONFIG_INVALID","ONE_TIME_PROFILE_REQUIRED","BILLING_PROFILE_REQUIRED","QR_PAYMENT_CONFIGURATION_INVALID"].includes(detail.code)) {
    const locale=(options.locale || (typeof localStorage!=="undefined"&&localStorage.getItem("lang")) || "ko").slice(0,2);
    const text={
      en:['Online payment is currently unavailable. Please contact the shop for help.','Online payment is currently unavailable. Please choose another payment method.','Payment for this order is currently unavailable. Please contact the shop for help.'],
      vi:['Hiện chưa thể thanh toán trực tuyến. Vui lòng liên hệ cửa hàng để được hỗ trợ.','Hiện chưa thể thanh toán trực tuyến. Vui lòng chọn phương thức thanh toán khác.','Hiện chưa thể thanh toán đơn hàng. Vui lòng liên hệ cửa hàng để được hỗ trợ.'],
      ko:['현재 온라인 결제를 이용할 수 없습니다. 매장에 문의해 주세요.','현재 온라인 결제를 이용할 수 없습니다. 다른 결제 수단을 선택해 주세요.','현재 이 주문을 결제할 수 없습니다. 매장에 문의해 주세요.']};
    const index=options.hasAlternativePayment===true?1:options.hasAlternativePayment===false?2:0;
    return {payload,code:detail.code,message:(text[locale]||text.ko)[index],context,retryable:false,isAuthFailure:false};
  }

  const messageKeys = [
    context.acceptance_message_key,
    context.message_key,
    detail.message_key,
    payload.message_key,
    context.reason,
    msg,
  ]
    .map(safeApiText)
    .filter(Boolean);

  let message = "";
  for (const key of messageKeys) {
    message = translatedText(key, translate, aliases);
    if (message) {
      break;
    }
  }

  const directMessages = [
    detail.message,
    payload.validate,
    payload.alert,
    payload.message,
    payload.ErrorMessage,
    payload.errorMessage,
    providerStatus.ErrorMessage,
    providerStatus.errorMessage,
    providerStatus.message,
    nestedProviderStatus.ErrorMessage,
    nestedProviderStatus.errorMessage,
    nestedProviderStatus.message,
  ];
  for (const candidate of directMessages) {
    if (!message) {
      message = safeApiText(candidate);
    }
  }

  const legacyError =
    typeof payload.error === "string" ? safeApiText(payload.error) : "";
  if (!message && legacyError) {
    message = translatedText(legacyError, translate, aliases);
  }
  if (!message && legacyError && /\s/.test(legacyError)) {
    message = legacyError;
  }

  if (!message && msg && /\s/.test(msg)) {
    message = msg;
  }

  if (!message && !Object.keys(payload).length) {
    message = safeApiText(source?.message);
  }

  const fallback =
    safeApiText(options.fallback) || "Unable to complete the request.";
  const reason =
    safeApiText(context.reason) ||
    safeApiText(context.acceptance_message_key) ||
    msg ||
    legacyError;
  const code =
    safeApiText(detail.code) ||
    safeApiText(payload.code) ||
    safeApiText(providerStatus.code) ||
    safeApiText(providerStatus.Code) ||
    safeApiText(providerStatus.ResultCode) ||
    safeApiText(nestedProviderStatus.code) ||
    safeApiText(nestedProviderStatus.Code) ||
    safeApiText(nestedProviderStatus.ResultCode) ||
    safeApiText(payload.ResultStatus) ||
    msg;

  return {
    code,
    reason,
    messageKey: messageKeys[0] || "",
    message: message || fallback,
    retryable: detail.retryable === true,
    context,
    payload,
  };
};

export const apiErrorMatches = (normalized, ...values) => {
  const candidates = [
    normalized?.code,
    normalized?.reason,
    normalized?.messageKey,
    normalized?.payload?.msg,
    normalized?.context?.acceptance_reason,
    normalized?.context?.acceptance_message_key,
  ]
    .map(safeApiText)
    .filter(Boolean);

  return values.some((value) => candidates.includes(value));
};
